import {
    Component,
    OnInit,
    Input,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ObjectType } from "../ReservationI";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Bind } from "../../../decorators/bind.decorator";
import { InstanceCount } from "../../../util/s25.instance.count";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25RadioModule } from "../../s25-radio/s25.radio.module";

@TypeManagerDecorator("s25-ng-ev-colander")
@Component({
    selector: "s25-ng-ev-colander",
    template: `
        @if (init && itemNames?.length > 0) {
            <div class="spacer-bottom">
                @let expandId = "ev-prefs_" + instanceId;
                <div class="s25-panel-head ev-prefs-wrapper">
                    <span
                        class="{{
                            type === 'location'
                                ? 's25-item-type-icon-holder-spaces'
                                : 's25-item-type-icon-holder-resources'
                        }}"
                    >
                        <span
                            class="{{ type === 'location' ? 'icon-space' : 'icon-resource' }} s25item-type-icon"
                        ></span>
                    </span>
                    <span class="ngBold">Filter By {{ type === "location" ? "Locations" : "Resources" }}</span>
                    <s25-ng-icon-button
                        class="floatRight"
                        [type]="isExpand ? 'caretDown' : 'caretUp'"
                        [ariaExpanded]="isExpand"
                        [ariaLabel]="isExpand ? 'Collapse' : 'Expand'"
                        [ariaControls]="isExpand ? expandId : undefined"
                        (clicked)="expandToggle(!isExpand)"
                    />
                </div>
                @if (isExpand) {
                    <div [id]="expandId" class="evdo-panel first">
                        <span>
                            <s25-ng-checkbox [(value)]="isSelectedAll" (valueChange)="selectAll()"
                                >Select All</s25-ng-checkbox
                            >
                        </span>
                        <span class="floatRight radio-wrapper">
                            <s25-ng-radio
                                [(value)]="filterType"
                                [radioValue]="'any'"
                                [name]="type"
                                (valueChange)="filter()"
                                >Any</s25-ng-radio
                            >
                            <s25-ng-radio
                                [(value)]="filterType"
                                [radioValue]="'all'"
                                [name]="type"
                                (valueChange)="filter()"
                                >All</s25-ng-radio
                            >
                        </span>
                    </div>
                    <div class="evdo-panel">
                        @for (d of itemNames; track d) {
                            <div>
                                <s25-ng-checkbox [value]="isChecked(d)" (valueChange)="onCheckBox($event, d)">
                                    {{ d }}</s25-ng-checkbox
                                >
                            </div>
                        }
                    </div>
                }
            </div>
        }
    `,
    styles: [
        `
            :host ::ng-deep .floatRight {
                float: right !important;
            }

            :host ::ng-deep .s25-panel-head {
                font-size: 0.875rem;
            }

            :host ::ng-deep .pref-panel {
                border-inline: 1px solid rgba(0, 0, 0, 0.15);
                border-bottom: 1px solid rgba(0, 0, 0, 0.15);
                font-size: 0.9rem;
                padding: 1rem;
            }

            :host ::ng-deep .evdo-panel {
                border-inline: 1px solid rgba(0, 0, 0, 0.15);
                padding: 10px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.15);
                font-size: 0.9rem;
            }

            :host ::ng-deep input[type="checkbox"] {
                border: 1px solid rgba(0, 0, 0, 0.25);
                border-radius: 3px;
                background-color: #fafaf9;
            }

            .spacer-bottom {
                margin-bottom: 20px;

                .first {
                    display: flex;
                    justify-content: space-between;

                    .radio-wrapper {
                        display: flex;
                        gap: 5px;
                    }
                }
            }

            :host ::ng-deep .nm-party--on s25-ng-ev-colander .ev-prefs-wrapper .evdo-panel {
                background-color: #3c3d46;
            }

            :host ::ng-deep .nm-party--on s25-ng-ev-colander .ev-prefs-wrapper {
                border: 1px solid rgba(255, 255, 255, 0.24);
                border-radius: 3px;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25IconButtonComponent, S25CheckboxModule, S25RadioModule],
})
export class S25EventColanderComponent extends InstanceCount implements OnInit {
    @Input() data?: S25ItemI[];
    @Input() type: ObjectType;
    @Input() noResources?: boolean;
    @Input() noLocations?: boolean;
    @Output() modelValueChange = new EventEmitter<{ filterType: "any" | "all"; itemList: string[] }>();

    isExpand: boolean = true;
    init: boolean = false;
    itemList: string[];
    isSelectedAll: boolean = true;
    filterType: "any" | "all" = "any";
    itemNames?: string[] = [];

    constructor(private cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.itemNames = [...new Set(this.data.map((item: any) => item.itemName))].sort();
        this.itemList = this.itemNames;
        if (this.noResources) this.itemList.unshift("No Resource");
        if (this.noLocations) this.itemList.unshift("No Location");
        this.init = true;
        this.cd.detectChanges();
    }

    expandToggle(e: boolean) {
        this.isExpand = e;
        this.cd.detectChanges();
    }

    selectAll() {
        if (this.isSelectedAll) {
            this.itemList = this.itemNames;
        } else {
            this.itemList = [];
        }

        this.modelValueChange.emit({ itemList: this.itemList, filterType: this.filterType });
        this.cd.detectChanges();
    }

    filter() {
        this.modelValueChange.emit({ itemList: this.itemList, filterType: this.filterType });
    }

    isChecked(name: string): boolean {
        return this.itemList.includes(name);
    }

    onCheckBox(checked: boolean, name?: string) {
        if (!checked) this.isSelectedAll = false;
        if (checked) {
            const find = this.itemList.filter((i: string) => {
                return i === name;
            });
            find.length === 0 ? this.itemList.push(name) : "";
        } else {
            this.itemList = this.itemList.filter((i: string) => {
                return i !== name;
            });
        }

        this.modelValueChange.emit({ itemList: this.itemList, filterType: this.filterType });
    }

    @Bind
    refresh(itemList: string[]) {
        this.itemNames = itemList;
        this.cd.detectChanges();
    }
}
